/**
 * Production-aware logger.
 *
 * Wraps console.log / .info / .debug with a guard that strips them in
 * production builds. console.warn and console.error always pass through
 * — those signal real problems users need to see in production tooling
 * like Sentry or the existing /api/errors/report pipeline.
 *
 * iter-165 — addresses the code-review finding that the app has 98+
 * console statements that clutter production logs and leak information.
 * Most are diagnostic / dev-time logs; switching to this utility lets
 * them stay in code (useful for debugging) but become no-ops in prod.
 *
 * Usage:
 *   import logger from "@/lib/logger";
 *   logger.log("[engine] mic capture ok", details);   // stripped in prod
 *   logger.info("[suno] task submitted", taskId);     // stripped in prod
 *   logger.debug(...)                                  // stripped in prod
 *   logger.warn("rate limit nearing", remaining);      // always logged
 *   logger.error("upload failed", err);                // always logged
 */
const isProd = process.env.NODE_ENV === "production";
// Explicit opt-in to dev logs in prod via ?debug=1 or
// localStorage.elpm_debug = "1" so on-site debugging stays possible.
const debugForced = (() => {
  try {
    if (typeof window === "undefined") return false;
    if (window.localStorage?.getItem("elpm_debug") === "1") return true;
    if (typeof window.location !== "undefined" && /[?&]debug=1\b/.test(window.location.search)) return true;
  } catch (e) { void e; }
  return false;
})();
const enabled = !isProd || debugForced;

const noop = () => {};

const logger = {
  log: enabled ? console.log.bind(console) : noop,
  info: enabled ? console.info.bind(console) : noop,
  debug: enabled ? console.debug.bind(console) : noop,
  // warn + error ALWAYS pass through — these signal real issues that
  // should reach Sentry / our error-reporting pipeline.
  warn: console.warn.bind(console),
  error: console.error.bind(console),
};

export default logger;
